import { useState } from "react";
import SectionHeading from "@/components/base/SectionHeading";
import Tag from "@/components/base/Tag";
import Reveal from "@/components/base/Reveal";
import { rankDaily, rankWeekly } from "@/mocks/homeData";

type RankRange = "daily" | "weekly";

export default function HotRanking() {
  const [range, setRange] = useState<RankRange>("daily");
  const list = range === "daily" ? rankDaily : rankWeekly;

  return (
    <section id="ranking" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-[1400px] px-4 md:px-6">
        <SectionHeading
          eyebrow="讨论热度榜"
          eyebrowIcon="ri-bar-chart-2-line"
          title="大家这两天都在聊什么"
          desc="热度由阅读、讨论与转发综合得出，每天凌晨重算一次。榜单位置会变，说明话题还在动。"
          action={
            <div className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
              <button
                type="button"
                onClick={() => setRange("daily")}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  range === "daily" ? "bg-foreground-950 text-background-50" : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                日榜
              </button>
              <button
                type="button"
                onClick={() => setRange("weekly")}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  range === "weekly" ? "bg-foreground-950 text-background-50" : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                周榜
              </button>
            </div>
          }
        />

        <div className="grid gap-10 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-8">
            <div>
              {list.map((item, index) => (
                <Reveal key={item.id} delay={index * 40}>
                  <article className="group flex items-center gap-3 border-b border-background-200 py-3.5 md:gap-5">
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md font-heading text-sm font-bold ${
                        index < 3
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-100 text-secondary-600"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <a
                        href="#ranking"
                        className="block truncate text-sm font-medium text-foreground-950 transition-colors group-hover:text-primary-600"
                      >
                        {item.title}
                      </a>
                      <div className="mt-1.5 flex items-center gap-3">
                        <Tag variant="secondary">{item.tag}</Tag>
                        <span className="hidden h-1.5 w-full max-w-[180px] overflow-hidden rounded-full bg-background-200 sm:block">
                          <span
                            className="block h-full rounded-full bg-accent-500"
                            style={{ width: `${item.heat}%` }}
                          ></span>
                        </span>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2 md:gap-4">
                      <span className="font-mono text-xs text-secondary-600">{item.heat}</span>
                      <span
                        className={`flex items-center gap-0.5 text-[11px] ${
                          item.trend === "up"
                            ? "text-primary-600"
                            : item.trend === "down"
                              ? "text-secondary-500"
                              : "text-secondary-500"
                        }`}
                      >
                        <span className="flex h-3.5 w-3.5 items-center justify-center">
                          <i
                            className={
                              item.trend === "up"
                                ? "ri-arrow-up-line"
                                : item.trend === "down"
                                  ? "ri-arrow-down-line"
                                  : "ri-subtract-line"
                            }
                          ></i>
                        </span>
                        <span className="hidden md:inline">{item.delta}</span>
                      </span>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-accent-50 p-5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-500 text-background-50">
                <i className="ri-trophy-line text-lg"></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">本周上升最快</h3>
              <p className="mt-2 text-xs leading-relaxed text-foreground-700">
                从无人讨论到冲进前十，只用了不到两天。热度波动最大的话题会标在这里。
              </p>
              <div className="mt-5 space-y-3 border-t border-accent-200 pt-4">
                {[
                  { name: "夜航船阿泽", value: "+68%", icon: "ri-live-line" },
                  { name: "甜心苏打", value: "+37%", icon: "ri-shopping-bag-3-line" },
                  { name: "桃子不吃桃", value: "+29%", icon: "ri-shirt-line" },
                ].map((row) => (
                  <div key={row.name} className="flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-50 text-primary-600">
                      <i className={row.icon}></i>
                    </span>
                    <span className="flex-1 text-sm text-foreground-800">{row.name}</span>
                    <span className="font-mono text-xs font-medium text-primary-600">{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}